import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ErrorDeLaApi, pedir } from './nucleo'

/**
 * La agencia revendedora (CLAUDE.md §36, fase R3): sus clientes y sus planes. Todo lo decide el núcleo
 * (`src/revendedores/clientes.ts`): aquí solo se pide y se enseña.
 */
export type FichaDeAgencia = {
  /** La bolsa de créditos de la agencia (R4): lo que gastan entre todos sus clientes. */
  bolsa: { total: number; delPlan: number; comprados: number }
  agencia: { id: number; nombre: string; slug: string; activo: boolean; direccion: string }
  techo: null | (Record<string, unknown> & { nombre: string; clientesMaximos: number; modulos: string[]; alcancesIAPermitidos: string[]; idiomas?: string[] })
  clientes: number
  puede: { clientes: boolean; planes: boolean; marca: boolean; cobros: boolean }
}

export type ClienteDeAgencia = {
  id: number
  nombre: string
  estado: 'activo' | 'suspendido'
  plan: null | { id: number; nombre: string }
  webs: number
  publicadas: number
  creditos: number
  /** Lo que puede gastar al mes de la bolsa: el suyo (`topePropio`) o el de su plan. */
  tope: number
  topePropio: boolean
  /** Lo que ha gastado de la bolsa en los últimos 30 días. */
  gastado30: number
  /** Lo que tiene pagado a la agencia (R5) y cómo va. */
  pagadoHasta: null | string
  pago: EstadoDePago
  suspendidaPorImpago: boolean
  propietarios: Array<{ nombre: null | string; email: string }>
  creado: string
}

export type EstadoDePago = 'al-dia' | 'en-gracia' | 'por-vencer' | 'sin-pagos' | 'vencido'

/** Cómo cobra la agencia a sus clientes (R5). El secreto de PayPal no viaja: solo su pista. */
export type CobroDeAgencia = {
  moneda: string
  monedas: string[]
  direccionDeAvisos: string
  paypal: { activo: boolean; configurado: boolean; modo: 'live' | 'sandbox'; clientId: null | string; webhookId: null | string; pista: null | string; sincronizado: boolean; monedaAdmitida: boolean }
  manuales: FormasManuales
  diasDeGracia: number
  suspenderAlVencer: boolean
}

export type FormasManuales = {
  yape: null | { numero: string; titular: null | string }
  plin: null | { numero: string; titular: null | string }
  transferencia: null | { banco: null | string; titular: null | string; cuenta: null | string; cci: null | string }
  instrucciones: null | string
}

export type Recibo = { id: number; numero: number; concepto: string; importe: number; moneda: string; metodo: string; referencia?: null | string; fecha: string; hasta?: string; cliente?: null | { id: number; nombre: string } }

export const METODOS: Record<string, string> = { paypal: 'PayPal', yape: 'Yape', plin: 'Plin', transferencia: 'Transferencia', efectivo: 'Efectivo', otro: 'Otro' }

export const importe = (centimos: number, moneda: string) => `${(centimos / 100).toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda}`

export type PlanDeAgencia = {
  id: number
  nombre: string
  precioMensual: null | number
  modulos: null | string[]
  alcancesIAPermitidos: null | string[]
  /** Los idiomas que sus webs pueden encender además del español (§37). */
  idiomas?: null | string[]
  sitiosMaximos: null | number
  creditosMensuales: null | number
  cuotaDeMediosMB: null | number
  clientes: number
} & Record<string, unknown>

/** La ficha de la agencia si este espacio es una; `null` si no lo es (o quien mira no la lleva). */
export const useFichaDeAgencia = (espacio: number) =>
  useQuery({
    queryKey: ['agencia', espacio],
    enabled: Number.isInteger(espacio),
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return await pedir<FichaDeAgencia>(`/panel/revendedor?cliente=${espacio}`)
      } catch (e) {
        if (e instanceof ErrorDeLaApi && e.status === 404) return null
        throw e
      }
    },
  })

export const useClientesDeAgencia = (espacio: number) =>
  useQuery({ queryKey: ['agencia', espacio, 'clientes'], queryFn: () => pedir<{ clientes: ClienteDeAgencia[] }>(`/panel/revendedor/clientes?cliente=${espacio}`).then((r) => r.clientes) })

export const usePlanesDeAgencia = (espacio: number) =>
  useQuery({ queryKey: ['agencia', espacio, 'planes'], queryFn: () => pedir<{ planes: PlanDeAgencia[] }>(`/panel/revendedor/planes?cliente=${espacio}`).then((r) => r.planes) })

export const useRefrescarAgencia = (espacio: number) => {
  const cliente = useQueryClient()
  return () => cliente.invalidateQueries({ queryKey: ['agencia', espacio] })
}

export const agencia = (espacio: number) => ({
  crearCliente: (datos: { nombre: string; plan: number; propietario: { email: string; nombre: string } }) =>
    pedir<{ cliente: { id: number; nombre: string }; mensaje: string; correoEnviado: boolean }>('/panel/revendedor/clientes', { metodo: 'POST', cuerpo: { cliente: espacio, ...datos } }),
  cambiarPlan: (cuenta: number, plan: number) => pedir('/panel/revendedor/clientes/plan', { metodo: 'POST', cuerpo: { cliente: espacio, cuenta, plan } }),
  creditos: (cuenta: number, datos: { extra?: number; tope?: null | number }) => pedir<{ ok: true; saldo: number }>('/panel/revendedor/clientes/creditos', { metodo: 'POST', cuerpo: { cliente: espacio, cuenta, ...datos } }),
  cambiarEstado: (cuenta: number, estado: 'activo' | 'suspendido') => pedir('/panel/revendedor/clientes/estado', { metodo: 'POST', cuerpo: { cliente: espacio, cuenta, estado } }),
  guardarPlan: (datos: Record<string, unknown>) => pedir<{ plan: PlanDeAgencia }>('/panel/revendedor/planes', { metodo: 'POST', cuerpo: { cliente: espacio, ...datos } }),
})

export const useCobroDeAgencia = (espacio: number, activo = true) =>
  useQuery({ queryKey: ['agencia', espacio, 'cobro'], enabled: activo, queryFn: () => pedir<CobroDeAgencia>(`/panel/revendedor/cobro?cliente=${espacio}`) })

export const useRecibosDeAgencia = (espacio: number) =>
  useQuery({ queryKey: ['agencia', espacio, 'recibos'], queryFn: () => pedir<{ cobros: Recibo[] }>(`/panel/revendedor/cobros?cliente=${espacio}`).then((r) => r.cobros) })

export const cobros = (espacio: number) => ({
  guardar: (datos: Record<string, unknown>) => pedir<CobroDeAgencia>('/panel/revendedor/cobro', { metodo: 'POST', cuerpo: { cliente: espacio, ...datos } }),
  probar: () => pedir<{ ok: true; suscripciones: boolean }>('/panel/revendedor/cobro/probar', { metodo: 'POST', cuerpo: { cliente: espacio } }),
  sincronizar: () => pedir<{ creados: number; alDia: number }>('/panel/revendedor/cobro/sincronizar', { metodo: 'POST', cuerpo: { cliente: espacio } }),
  apuntar: (datos: { cuenta: number; meses: number; importe?: string; metodo: string; referencia?: string }) => pedir<{ recibo: Recibo }>('/panel/revendedor/cobros', { metodo: 'POST', cuerpo: { cliente: espacio, ...datos } }),
})

/** Lo que ve el cliente de una agencia: a quién paga, cómo y sus recibos. `null` si su cuenta no es de una agencia. */
export type MiProveedor = {
  agencia: { nombre: string; soporte: null | Record<string, unknown> }
  plan: null | { nombre: string; precio: number; moneda: string }
  pagadoHasta: null | string
  estado: EstadoDePago
  suspendidaPorImpago: boolean
  formas: FormasManuales & { paypal: boolean }
  suscripcion: null | { id: string; estado: string; hasta: null | string }
  recibos: Recibo[]
}

export const useMiProveedor = (espacio: number, activo = true) =>
  useQuery({
    queryKey: ['mi-proveedor', espacio],
    enabled: activo && Number.isInteger(espacio),
    queryFn: async () => {
      try {
        return await pedir<MiProveedor>(`/panel/mi-proveedor?cliente=${espacio}`)
      } catch (e) {
        if (e instanceof ErrorDeLaApi && e.status === 404) return null
        throw e
      }
    },
  })

export const miProveedor = (espacio: number) => ({
  suscribirse: () => pedir<{ aprobar: string; suscripcion: string }>('/panel/mi-proveedor/suscribirse', { metodo: 'POST', cuerpo: { cliente: espacio } }),
  confirmar: (suscripcion: string) => pedir<{ estado: string }>('/panel/mi-proveedor/confirmar', { metodo: 'POST', cuerpo: { cliente: espacio, suscripcion } }),
  cancelar: () => pedir<{ ok: true }>('/panel/mi-proveedor/cancelar', { metodo: 'POST', cuerpo: { cliente: espacio } }),
})

/** «Mi marca y dominios» de la agencia (R6). Lo que dice el núcleo, tal cual. */
export type EstadoDelPanel = 'certificado' | 'conectado' | 'conectando' | 'esperando-dns' | 'problema'
export type EstadoDeWebs = 'apunta' | 'no-apunta' | 'sin-comprobar' | 'sin-registros' | 'sin-respuesta'
export type MarcaDeAgencia = {
  nombre: string
  direccionDelPanel: string
  subdominio: string
  marca: { logo?: null | string; logoOscuro?: null | string; favicon?: null | string; colorPrincipal?: null | string; lema?: null | string }
  soporte: { correo?: null | string; url?: null | string; whatsapp?: null | string }
  correo: { nombreRemitente?: null | string; responderA?: null | string }
  datosFiscales: { razonSocial?: null | string; idFiscal?: null | string; direccion?: null | string; pais?: null | string }
  dominioPanel: null | { dominio: string; estado: EstadoDelPanel; problema: null | string; comprobadoEn: null | string; registro: { tipo: 'CNAME'; nombre: string; valor: string } }
  dominioWebs: null | { dominio: string; estado: EstadoDeWebs; comprobadoEn: null | string; registro: { tipo: 'CNAME'; nombre: string; valor: string }; ejemplo: string }
  registroPublico: boolean
  planDeRegistro: null | number
  planes: Array<{ id: number; nombre: string }>
}

export const useMarcaDeAgencia = (espacio: number, activo = true) =>
  useQuery({ queryKey: ['agencia', espacio, 'marca'], enabled: activo, queryFn: () => pedir<MarcaDeAgencia>(`/panel/revendedor/marca?cliente=${espacio}`) })

export const marcaDeAgencia = (espacio: number) => ({
  guardar: (datos: Record<string, unknown>) => pedir<MarcaDeAgencia>('/panel/revendedor/marca', { metodo: 'POST', cuerpo: { cliente: espacio, ...datos } }),
  comprobar: () => pedir<MarcaDeAgencia>('/panel/revendedor/marca/comprobar', { metodo: 'POST', cuerpo: { cliente: espacio } }),
  imagen: async (campo: 'favicon' | 'logo' | 'logoOscuro', fichero: File | null) => {
    const datos = new FormData()
    datos.set('cliente', String(espacio))
    datos.set('campo', campo)
    if (fichero) datos.set('fichero', fichero)
    const r = await fetch('/api/panel/revendedor/marca/imagen', { method: 'POST', body: datos, credentials: 'same-origin' })
    const json = (await r.json().catch(() => ({}))) as Record<string, unknown>
    if (!r.ok) throw new ErrorDeLaApi(typeof json.mensaje === 'string' ? json.mensaje : Array.isArray(json.errors) ? String((json.errors as Array<{ message?: string }>)[0]?.message ?? 'No se ha podido subir la imagen.') : 'No se ha podido subir la imagen.', r.status)
    return json as unknown as MarcaDeAgencia
  },
})
